import { useCallback, useEffect, useState } from "react";
import type {
  BrandingTemplate,
  EntityChangedEvent,
  Event,
  Leaderboard,
  LeaderboardEntry,
  RaceStanding,
  VenueLeaderboardEntry,
  VenueLeaderboardQuery,
} from "@vms/types";
import { useApi } from "../auth/AuthProvider";
import { useInvalidateOn } from "./useInvalidateOn";
import { subscribeToVenue, useVenueSubscription } from "./ws";

interface QueryState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export interface LeaderboardConfig {
  id: string;
  organisationId: string;
  venueId: string | null;
  name: string;
  brandingTemplateId: string | null;
  eventIds: string[];
  tracks: string[];
  cars: string[];
  game: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  entryLimit: number | null;
  subheading: string | null;
  /** Cloud-only: present when the row is cloud-authored (venueId is null).
   *  null = all org venues; non-empty array = explicit subset. Undefined on
   *  hub-authored rows. See packages/types/src/entities.ts:Leaderboard. */
  venueIds?: string[] | null;
  createdByUserId?: string | null;
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Internal: hand-rolled fetcher (cloud-ui has no react-query). Mirrors the
 * shape of `useQuery` in `./queries.ts` but accepts a parametric fetcher so
 * leaderboard hooks can be stable across filter changes.
 */
function useLeaderboardQuery<T>(
  enabled: boolean,
  fetcher: () => Promise<T | null>,
  deps: unknown[],
): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcher()
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tick, ...deps]);

  return {
    data,
    loading,
    error,
    refetch: () => setTick((t) => t + 1),
  };
}

/**
 * Single leaderboard config. Used by the display page to read the saved
 * filters (eventIds, tracks, cars, dateFrom/To, entryLimit, branding) before
 * passing them into `useVenueLeaderboard`.
 */
export function useLeaderboardRecord(id: string | undefined): QueryState<LeaderboardConfig> {
  const api = useApi();
  const query = useLeaderboardQuery<LeaderboardConfig>(
    Boolean(id),
    async () => {
      if (!id) return null;
      const res = await api(`/api/leaderboard/config/${id}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      return (json.data as LeaderboardConfig) ?? null;
    },
    [id],
  );
  useInvalidateOn(
    "leaderboard",
    query.refetch,
    useCallback((m: EntityChangedEvent) => !id || m.id === id, [id]),
  );
  return query;
}

/**
 * Venue-wide ranking. Subscribes the connection to the venue's broadcast
 * room so lap-time deltas trigger a refetch in real time.
 */
export function useVenueLeaderboard(
  venueId: string | undefined,
  filters: VenueLeaderboardQuery | null,
): QueryState<VenueLeaderboardEntry[]> {
  const api = useApi();
  useVenueSubscription(venueId);

  const enabled = Boolean(venueId) && filters !== null;
  const f = filters ?? {};
  const tracksKey = JSON.stringify(f.tracks ?? []);
  const carsKey = JSON.stringify(f.cars ?? []);
  const eventIdsKey = JSON.stringify(f.eventIds ?? []);
  const gameKey = f.game ?? "";
  const dateFromKey = f.dateFrom ?? "";
  const dateToKey = f.dateTo ?? "";
  const limitKey = f.limit ?? 0;

  const query = useLeaderboardQuery<VenueLeaderboardEntry[]>(
    enabled,
    async () => {
      if (!venueId) return [];
      const params = new URLSearchParams();
      params.set("venueId", venueId);
      if (f.tracks?.length) params.set("tracks", f.tracks.join(","));
      if (f.cars?.length) params.set("cars", f.cars.join(","));
      if (f.eventIds?.length) params.set("eventIds", f.eventIds.join(","));
      if (f.game) params.set("game", f.game);
      if (f.dateFrom) params.set("dateFrom", f.dateFrom);
      if (f.dateTo) params.set("dateTo", f.dateTo);
      if (f.limit) params.set("limit", String(f.limit));
      const res = await api(`/api/leaderboard/venue?${params.toString()}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      return (json.data as VenueLeaderboardEntry[]) ?? [];
    },
    [venueId, tracksKey, carsKey, eventIdsKey, gameKey, dateFromKey, dateToKey, limitKey],
  );

  useInvalidateOn(
    "leaderboard",
    query.refetch,
    useCallback(
      (m: EntityChangedEvent) =>
        !venueId || m.scope?.venueId === venueId || !m.scope?.venueId,
      [venueId],
    ),
  );
  return query;
}

/**
 * Branding template lookup. The cloud's `branding_templates` table is at
 * column-parity with the hub's after migration 010, so the cloud-ui
 * consumes the same `BrandingTemplate` shape `@vms/leaderboard-ui`'s
 * `BrandingProvider` expects.
 */
export function useBrandingTemplate(id: string | null | undefined): QueryState<BrandingTemplate> {
  const api = useApi();
  const query = useLeaderboardQuery<BrandingTemplate>(
    Boolean(id),
    async () => {
      if (!id) return null;
      const res = await api(`/api/branding-templates/${id}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      return (json.data as BrandingTemplate) ?? null;
    },
    [id],
  );
  useInvalidateOn(
    "branding-template",
    query.refetch,
    useCallback((m: EntityChangedEvent) => !id || m.id === id, [id]),
  );
  return query;
}

/** Per-event hotlap leaderboard. Refetches when a lap-time lands for the
 *  matching event. */
export function useEventLeaderboard(
  venueId: string | undefined,
  eventId: string | undefined,
): QueryState<LeaderboardEntry[]> {
  const api = useApi();
  useVenueSubscription(venueId);

  const query = useLeaderboardQuery<LeaderboardEntry[]>(
    Boolean(eventId),
    async () => {
      if (!eventId) return [];
      const res = await api(`/api/leaderboard/by-event/${eventId}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      return (json.data as LeaderboardEntry[]) ?? [];
    },
    [eventId],
  );
  useInvalidateOn(
    "leaderboard",
    query.refetch,
    useCallback(
      (m: EntityChangedEvent) =>
        !eventId || m.scope?.eventId === eventId || m.id === eventId,
      [eventId],
    ),
  );
  return query;
}

/**
 * Single event lookup. Used by the display pages to read event.name (page
 * title), event.track (subtitle), and event.brandingTemplateId (which template
 * to apply). Refetches when the event entity changes (e.g. operator renames it
 * or swaps the branding template mid-event).
 */
export function useEvent(eventId: string | undefined): QueryState<Event> {
  const api = useApi();
  const query = useLeaderboardQuery<Event>(
    Boolean(eventId),
    async () => {
      if (!eventId) return null;
      const res = await api(`/api/events/${eventId}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      return (json.data as Event) ?? null;
    },
    [eventId],
  );
  useInvalidateOn(
    "event",
    query.refetch,
    useCallback(
      (m: EntityChangedEvent) => !eventId || m.id === eventId,
      [eventId],
    ),
  );
  return query;
}

/** Per-event race standings. Position by laps-completed desc, then total time
 *  asc. Refetches when a lap-time lands for the matching event. */
export function useEventRaceStandings(
  venueId: string | undefined,
  eventId: string | undefined,
): QueryState<RaceStanding[]> {
  const api = useApi();
  useVenueSubscription(venueId);

  const query = useLeaderboardQuery<RaceStanding[]>(
    Boolean(eventId),
    async () => {
      if (!eventId) return [];
      const res = await api(`/api/leaderboard/by-event/${eventId}/race`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      return (json.data as RaceStanding[]) ?? [];
    },
    [eventId],
  );
  useInvalidateOn(
    "leaderboard",
    query.refetch,
    useCallback(
      (m: EntityChangedEvent) =>
        !eventId || m.scope?.eventId === eventId || m.id === eventId,
      [eventId],
    ),
  );
  return query;
}

/** Per-event fastest sectors. Highlight basis for the row sector cells.
 *  Refetches on the same trigger as the leaderboard / standings. */
export function useEventFastestSectors(
  venueId: string | undefined,
  eventId: string | undefined,
): QueryState<{
  sector1Ms: number | null;
  sector2Ms: number | null;
  sector3Ms: number | null;
}> {
  const api = useApi();
  useVenueSubscription(venueId);

  const query = useLeaderboardQuery<{
    sector1Ms: number | null;
    sector2Ms: number | null;
    sector3Ms: number | null;
  }>(
    Boolean(eventId),
    async () => {
      if (!eventId) return null;
      const res = await api(`/api/leaderboard/by-event/${eventId}/sectors`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      return (json.data as {
        sector1Ms: number | null;
        sector2Ms: number | null;
        sector3Ms: number | null;
      }) ?? null;
    },
    [eventId],
  );
  useInvalidateOn(
    "leaderboard",
    query.refetch,
    useCallback(
      (m: EntityChangedEvent) =>
        !eventId || m.scope?.eventId === eventId || m.id === eventId,
      [eventId],
    ),
  );
  return query;
}

// ── Cross-venue (cloud-authored) leaderboards ───────────────────────────────

/**
 * Subscribe to multiple venue WS rooms at once. v1 simplification — instead
 * of an org-scoped server-side room we just register the page interest in
 * every venue the leaderboard spans, so any of those venues' lap-time
 * deltas trigger an `entity-changed` envelope our `useInvalidateOn` listener
 * picks up. Cheap: each call is just a WS subscribe message and the cloud
 * coalesces identical subs per socket.
 */
export function useVenueSubscriptions(venueIds: string[] | undefined): void {
  // Stable join so the effect doesn't fire on array identity changes.
  const key = venueIds?.join(",") ?? "";
  useEffect(() => {
    if (!key) return;
    const ids = key.split(",");
    const cleanups = ids.map((id) => subscribeToVenue(id));
    return () => {
      for (const c of cleanups) c();
    };
  }, [key]);
}

/**
 * Org-level leaderboard list — cloud-authored only (the per-venue page handles
 * hub-authored rows). Returns the full Leaderboard entity shape so the list
 * UI can show name, scope summary, branding indicator, etc.
 */
export function useOrgLeaderboards(
  organisationId: string | undefined,
  options?: { includeArchived?: boolean },
): QueryState<Leaderboard[]> {
  const api = useApi();
  const includeArchived = options?.includeArchived ? "true" : "false";
  const query = useLeaderboardQuery<Leaderboard[]>(
    Boolean(organisationId),
    async () => {
      if (!organisationId) return [];
      const params = new URLSearchParams({ organisationId });
      if (includeArchived === "true") params.set("includeArchived", "true");
      const res = await api(`/api/leaderboards?${params.toString()}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      return (json.data as Leaderboard[]) ?? [];
    },
    [organisationId, includeArchived],
  );
  // Refetch on any leaderboard-entity change; the list is short.
  useInvalidateOn("leaderboard", query.refetch);
  return query;
}

/**
 * Multi-venue ranking — sibling of `useVenueLeaderboard` for cloud-authored
 * leaderboards. Pass either an explicit `venueIds` array (the leaderboard's
 * subset) or an `organisationId` (server resolves to all org venues).
 *
 * Disabled when scope is null. WS subscription tracks every venue in
 * `venueIds`; for the all-org case the caller resolves the venue list and
 * supplies it to `useVenueSubscriptions` separately.
 */
export function useMultiVenueLeaderboard(
  scope:
    | { venueIds: string[]; organisationId?: undefined }
    | { venueIds?: undefined; organisationId: string }
    | null,
  filters: VenueLeaderboardQuery | null,
): QueryState<VenueLeaderboardEntry[]> {
  const api = useApi();
  const explicitVenueIds = scope && "venueIds" in scope ? scope.venueIds : undefined;
  useVenueSubscriptions(explicitVenueIds);

  const enabled = Boolean(scope) && filters !== null;
  const f = filters ?? {};
  const venueIdsKey = explicitVenueIds ? explicitVenueIds.join(",") : "";
  const orgIdKey = scope && "organisationId" in scope ? scope.organisationId ?? "" : "";
  const tracksKey = JSON.stringify(f.tracks ?? []);
  const carsKey = JSON.stringify(f.cars ?? []);
  const eventIdsKey = JSON.stringify(f.eventIds ?? []);
  const gameKey = f.game ?? "";
  const dateFromKey = f.dateFrom ?? "";
  const dateToKey = f.dateTo ?? "";
  const limitKey = f.limit ?? 0;

  const query = useLeaderboardQuery<VenueLeaderboardEntry[]>(
    enabled,
    async () => {
      if (!scope) return [];
      const params = new URLSearchParams();
      if (venueIdsKey) {
        params.set("venueIds", venueIdsKey);
      } else if (orgIdKey) {
        params.set("organisationId", orgIdKey);
      } else {
        return [];
      }
      if (f.tracks?.length) params.set("tracks", f.tracks.join(","));
      if (f.cars?.length) params.set("cars", f.cars.join(","));
      if (f.eventIds?.length) params.set("eventIds", f.eventIds.join(","));
      if (f.game) params.set("game", f.game);
      if (f.dateFrom) params.set("dateFrom", f.dateFrom);
      if (f.dateTo) params.set("dateTo", f.dateTo);
      if (f.limit) params.set("limit", String(f.limit));
      const res = await api(`/api/leaderboard/venue?${params.toString()}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      return (json.data as VenueLeaderboardEntry[]) ?? [];
    },
    [
      venueIdsKey,
      orgIdKey,
      tracksKey,
      carsKey,
      eventIdsKey,
      gameKey,
      dateFromKey,
      dateToKey,
      limitKey,
    ],
  );

  useInvalidateOn(
    "leaderboard",
    query.refetch,
    useCallback(
      (m: EntityChangedEvent) => {
        if (!explicitVenueIds || explicitVenueIds.length === 0) return true;
        return !m.scope?.venueId || explicitVenueIds.includes(m.scope.venueId);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [venueIdsKey],
    ),
  );
  return query;
}

// ── Mutations: create / update / delete cloud-authored leaderboards ─────────

interface MutateState<TArgs, TResult = Leaderboard> {
  mutate: (args: TArgs) => Promise<TResult>;
  loading: boolean;
  error: string | null;
}

interface CreateLeaderboardArgs {
  organisationId: string;
  name: string;
  brandingTemplateId?: string | null;
  venueIds?: string[] | null;
  eventIds?: string[];
  tracks?: string[];
  cars?: string[];
  game?: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  entryLimit?: number | null;
  subheading?: string | null;
}

export function useCreateLeaderboard(): MutateState<CreateLeaderboardArgs> {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return {
    loading,
    error,
    mutate: async (args) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/leaderboards`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(args),
        });
        const json = await res.json();
        if (!res.ok || !json.success) {
          throw new Error(json.error ?? `HTTP ${res.status}`);
        }
        return json.data as Leaderboard;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
  };
}

type UpdateLeaderboardArgs = { id: string } & Partial<
  Omit<CreateLeaderboardArgs, "organisationId">
>;

export function useUpdateLeaderboard(): MutateState<UpdateLeaderboardArgs> {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return {
    loading,
    error,
    mutate: async ({ id, ...patch }) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/leaderboards/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        });
        const json = await res.json();
        if (!res.ok || !json.success) {
          throw new Error(json.error ?? `HTTP ${res.status}`);
        }
        return json.data as Leaderboard;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
  };
}

export function useDeleteLeaderboard(): MutateState<{ id: string }> {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return {
    loading,
    error,
    mutate: async ({ id }) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/leaderboards/${id}`, { method: "DELETE" });
        const json = await res.json();
        if (!res.ok || !json.success) {
          throw new Error(json.error ?? `HTTP ${res.status}`);
        }
        return json.data as Leaderboard;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
  };
}

export interface PushLeaderboardArgs {
  id: string;
  displayAgentIds: string[];
}

export interface PushLeaderboardResult {
  hubsNotified: number;
  displaysScheduled: number;
  hubsOffline: string[];
  rejected: string[];
}

/** POST /api/leaderboards/:id/push — fan a cloud leaderboard to one or more
 *  display-agents across the org's venues. Cloud mints/refreshes a 7-day
 *  display-access token and sends each hub a `remote-set-composition`. */
export function usePushLeaderboardToDisplays(): MutateState<PushLeaderboardArgs, PushLeaderboardResult> {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return {
    loading,
    error,
    mutate: async ({ id, displayAgentIds }) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/leaderboards/${id}/push`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ displayAgentIds }),
        });
        const json = await res.json();
        if (!res.ok || !json.success) {
          throw new Error(json.error ?? `HTTP ${res.status}`);
        }
        return json.data as PushLeaderboardResult;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
  };
}
